import { useCallback, useState } from 'react';

/**
 * State mirrored into localStorage, so it survives a reload, a tab switch and re-entering the page from
 * anywhere. Use this for a personal *view preference*; use useUrlParamState when the value describes what
 * is being looked at and should travel in a shared link.
 *
 *   const [includeDuplicates, setIncludeDuplicates] = usePersistedState('manageAssignments:includeDuplicates', false);
 *
 * Namespace keys as `<feature>:<name>`, matching useMediaView's `media:view`. Every storage access is
 * guarded: private mode and a full quota both throw, and neither should break the page — the value just
 * falls back to in-memory state for the session.
 */
const usePersistedState = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored == null ? defaultValue : JSON.parse(stored);
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (next) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage unavailable — the in-memory value still works for this session.
      }
    },
    [key],
  );

  return [value, set];
};

export default usePersistedState;
